<script>
  import { onMount, onDestroy } from "svelte";
  import { lang } from '../lib/stores';
  import { scaleLinear } from "d3-scale";
  import { select } from "d3-selection";
  import { extent } from "d3";

  let selectedLang;
	const unsubscribe = lang.subscribe((value) => selectedLang = value);

  export let data = [];
  export let title = selectedLang === 'it' ? "Numero di ripetizioni →" : "Occurrences →";
  export let presetHighlight = false;

  const margins = [50, 50, 30, 2];
  const blockHeight = 40;
  const highlightedBlockHeight = 52;

  let svgObject;
  let width = 0;
  let svgWidth = 0;
  let svgHeight = 0;

  const draw = () => {
    if (data.length > 0) {
      svgWidth = width;
      svgHeight =
        data.filter((d) => [...new Set(d.term.split(" "))].length === 1)
          .length *
          highlightedBlockHeight +
        data.filter((d) => [...new Set(d.term.split(" "))].length !== 1)
          .length *
          blockHeight +
        margins[0] +
        margins[2];

      const svg = select(svgObject);
      svg.selectAll("g").remove();

      const gAxisTop = svg
        .append("g")
        .attr("transform", `translate(${margins[3]}, 0)`);

      const gAxisBottom = svg
        .append("g")
        .attr(
          "transform",
          `translate(${margins[3]}, ${svgHeight - margins[2]})`,
        );
      const gGrid = svg
        .append("g")
        .attr("transform", `translate(${margins[3]}, ${margins[0] - 10})`);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margins[3]}, ${margins[0]})`);

      const dataExtent = extent(data, (d) => d.count);
      dataExtent[0] = 0;
      if (!dataExtent[1] % 10 === 0) {
        dataExtent[1] = Math.ceil(dataExtent[1] / 10) * 10;
      }
      const x = scaleLinear()
        .range([0, svgWidth - margins[1] - margins[3]])
        .domain(dataExtent);

      const numberOfTicks = Math.floor(dataExtent[1] / 10);
      const tickWidth = Math.floor(dataExtent[1] / numberOfTicks);
      const ticks = Array(numberOfTicks)
        .fill(1)
        .map((d, i) => i * tickWidth);

      let prevY = 0;
      data.forEach((d) => {
        let highlight = d?.highlight ?? false;
        if (presetHighlight) {
          highlight = [...new Set(d.term.split(" "))].length === 1;
        }
        const rectY = highlight ? 20 : 16;
        const rectHeight = highlight ? 16 : 10;
        const width = x(d.count);
        const bar = g.append("g").attr("transform", `translate(0, ${prevY})`);

        bar
          .append("text")
          .attr("x", 0)
          .attr("y", 0)
          .attr("dx", 5)
          .attr("class", `bars-bar-label ${highlight ? "highlight" : "data"}`)
          .attr("dominant-baseline", "hanging")
          .attr("text-anchor", "start")
          .attr("fill", "black")
          .style("fill", highlight ? "var(--accent-2)" : "var(--foreground)")
          .style("font-size", highlight ? "16px" : "12px")
          .text(d.term);

        bar
          .append("rect")
          .attr("x", 0)
          .attr("y", rectY)
          .attr("width", width)
          .attr("height", rectHeight)
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .attr("fill", highlight ? "url(#dots-accent-2)" : "url(#dots-grey-0)")
          .style("stroke", highlight ? "var(--accent-2)" : "var(--grey)");

        bar
          .append("text")
          .attr("x", width)
          .attr("y", rectY + rectHeight / 2)
          .attr("dx", 5)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("class", `bars-bar-value ${highlight ? "highlight" : "data"}`)
          .attr("fill", "black")
          .style("fill", highlight ? "var(--accent-2)" : "var(--foreground)")
          .text(new Intl.NumberFormat("it-IT").format(d.count));

        prevY += highlight ? highlightedBlockHeight : blockHeight;
      });

      // Axis Block
      {
        gAxisTop
          .append("text")
          .attr("x", 0)
          .attr("y", 0)
          .attr("dominant-baseline", "hanging")
          .attr("text-anchor", "start")
          .attr("class", "bars-title data")
          .attr("fill", "black")
          .style("fill", "var(--foreground)")
          .text(title);
        gAxisTop
          .append("line")
          .attr("x1", 0)
          .attr("x2", x(dataExtent[1]))
          .attr("y1", margins[0] - 10)
          .attr("y2", margins[0] - 10)
          .attr("stroke", "black")
          .attr("strok-width", 1)
          .attr("class", "bars-axis-line")
          .style("stroke", "var(--foreground)");
        gAxisBottom
          .append("line")
          .attr("x1", 0)
          .attr("x2", x(dataExtent[1]))
          .attr("y1", 0)
          .attr("y2", 0)
          .attr("stroke", "black")
          .attr("strok-width", 1)
          .attr("class", "bars-axis-line")
          .style("stroke", "var(--foreground)");
      }
      ticks.forEach((t, i) => {
        let val = t;
        if (dataExtent[1] - t < 25) {
          val = dataExtent[1];
        }
        const xPos = x(val);
        gAxisTop
          .append("line")
          .attr("x1", xPos)
          .attr("x2", xPos)
          .attr("y1", margins[0] - 10)
          .attr("y2", margins[0] - 15)
          .attr("stroke", "black")
          .attr("strok-width", 1)
          .attr("class", "bars-axis-line")
          .style("stroke", "var(--foreground)");
        gAxisBottom
          .append("line")
          .attr("x1", xPos)
          .attr("x2", xPos)
          .attr("y1", 0)
          .attr("y2", 0 + 5)
          .attr("stroke", "black")
          .attr("strok-width", 1)
          .attr("class", "bars-axis-line")
          .style("stroke", "var(--foreground)");
        gGrid
          .append("line")
          .attr("x1", xPos)
          .attr("x2", xPos)
          .attr("y1", 0)
          .attr("y2", svgHeight - margins[0] - margins[2] + 10)
          .attr("stroke", "black")
          .attr("strok-width", 1)
          .attr("class", "bars-axis-line")
          .style("stroke", "var(--grey)")
          .style("stroke-dasharray", "1 2");
        gAxisTop
          .append("text")
          .attr("x", xPos)
          .attr("y", margins[0] - 15)
          .attr("dy", -5)
          .attr("fill", "black")
          .attr("text-anchor", val === dataExtent[1] ? "end" : "start")
          .attr("dominant-baseline", "auto")
          .attr("class", "bars-axis-label data")
          .style("fill", "var(--foreground)")
          .text(new Intl.NumberFormat("it-IT").format(val));
        gAxisBottom
          .append("text")
          .attr("x", xPos)
          .attr("y", 0)
          .attr("dy", 5)
          .attr("fill", "black")
          .attr("text-anchor", val === dataExtent[1] ? "end" : "start")
          .attr("dominant-baseline", "hanging")
          .attr("class", "bars-axis-label data")
          .style("fill", "var(--foreground)")
          .text(new Intl.NumberFormat("it-IT").format(val));
      });
    }
  };

  onDestroy(unsubscribe);
  onMount(() => {
    draw();
  });

  $: data, draw();
  $: width, draw();
  $: selectedLang, draw();
</script>

<section bind:clientWidth={width} class="bars">
  <svg
    bind:this={svgObject}
    width={svgWidth}
    height={svgHeight}
    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
    class="chart-svg-bars"
  />
</section>

<style>
  .bars {
    display: block;
  }
</style>
